import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, FileDown, Printer, Trash2, MessageCircle, ShoppingCart } from "lucide-react";
import { http, formatSoles, fileUrl } from "@/api";
import { useStore } from "@/context/StoreContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader, Panel, Confirm, ItemsEditor, QUOTE_STATUS, quoteStatus, fmtDateTime } from "./ui";
import { TotalsBlock, totalsOf } from "./OrderDetail";
import { quotePdf } from "./docs";

const SKIP = ["name", "whatsapp", "customer_type", "event_date", "district"];
const show = (v) => (Array.isArray(v) ? v.join(", ") : v);

export default function QuoteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { settings } = useStore();
  const [qd, setQd] = useState(null);
  const [orig, setOrig] = useState("");
  const [fields, setFields] = useState([]);
  const [products, setProducts] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    http.get(`/quotes/${id}`).then((r) => { setQd(r.data); setOrig(JSON.stringify(r.data)); }).catch(() => navigate("/admin/cotizaciones"));
    http.get("/quote-form").then((r) => setFields(r.data.fields || []));
    http.get("/products", { params: { active_only: false } }).then((r) => setProducts(r.data));
  }, [id, navigate]);

  const dirty = useMemo(() => qd && JSON.stringify(qd) !== orig, [qd, orig]);
  if (!qd) return <div className="text-neutral-500">Cargando…</div>;
  const set = (k, v) => setQd((x) => ({ ...x, [k]: v }));
  const ans = (fid) => qd.answers?.[fid] ?? qd[fid];

  async function save(extra = {}) {
    setSaving(true);
    try {
      const { data } = await http.patch(`/quotes/${qd.id}`, { ...qd, ...extra });
      setQd(data); setOrig(JSON.stringify(data));
      toast.success("Cotización guardada");
      return data;
    } catch { toast.error("Error al guardar"); } finally { setSaving(false); }
  }
  async function convert() {
    const saved = await save();
    if (!saved) return;
    try {
      const { data } = await http.post(`/quotes/${qd.id}/convert`);
      toast.success(`Pedido ${data.order_code} creado`);
      navigate(`/admin/pedidos/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo convertir");
    }
  }
  async function remove() { await http.delete(`/quotes/${qd.id}`); toast.success("Eliminada"); navigate("/admin/cotizaciones"); }
  async function sendWa() {
    const t = totalsOf(qd);
    const lines = [
      `*Cotización ${qd.quote_code || ""} — ${settings?.business_name || "Floral Rossé"}*`, `Hola ${qd.name}, te enviamos tu cotización:`, "",
      ...(qd.items || []).map((i) => `• ${i.qty}x ${i.name} — ${formatSoles(i.unit_price)} c/u`), "",
      qd.discount ? `Descuento: -${formatSoles(qd.discount)}` : "", qd.shipping_cost ? `Envío: ${formatSoles(qd.shipping_cost)}` : "",
      `*Total: ${formatSoles(t.total)}*`, qd.admin_notes ? `\n${qd.admin_notes}` : "",
    ].filter((l) => l !== "");
    window.open(`https://wa.me/${(qd.whatsapp || "").replace(/\D/g, "")}?text=${encodeURIComponent(lines.join("\n"))}`, "_blank");
    if (quoteStatus(qd.status).value === "pendiente") await save({ status: "enviada" });
  }

  const detailFields = fields.filter((f) => !SKIP.includes(f.id));
  const extraKeys = Object.keys(qd.answers || {}).filter((k) => !fields.some((f) => f.id === k) && !SKIP.includes(k));

  return (
    <div data-testid="admin-quote-detail">
      <Link to="/admin/cotizaciones" className="text-sm text-neutral-500 flex items-center gap-1 mb-3"><ArrowLeft size={14} /> Cotizaciones</Link>
      <PageHeader title={`Cotización ${qd.quote_code || ""}`} subtitle={`Recibida ${fmtDateTime(qd.created_at)} · ${qd.source === "manual" ? "creada manualmente" : "desde la web"}`}
        actions={<>
          <Button variant="outline" onClick={() => quotePdf(qd, settings, "print")}><Printer size={14} className="mr-1" /> Imprimir</Button>
          <Button variant="outline" onClick={() => quotePdf(qd, settings)} data-testid="quote-pdf"><FileDown size={14} className="mr-1" /> PDF</Button>
          <Button variant="outline" onClick={sendWa} disabled={!qd.whatsapp} data-testid="quote-send-wa"><MessageCircle size={14} className="mr-1" /> Enviar</Button>
          <Confirm onConfirm={remove} title="¿Eliminar esta cotización?" trigger={<Button variant="outline" className="text-red-600" data-testid="quote-delete"><Trash2 size={14} /></Button>} />
          <Button className="btn-primary" onClick={() => save()} disabled={!dirty || saving} data-testid="quote-save"><Save size={14} className="mr-1" /> {dirty ? "Guardar cambios" : "Guardado"}</Button>
        </>} />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Panel title="Lo que solicitó el cliente">
            {detailFields.length + extraKeys.length === 0 && <p className="text-sm text-neutral-500">Sin detalles.</p>}
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="quote-answers">
              {detailFields.map((f) => {
                const v = ans(f.id);
                if (f.type === "file") {
                  return (
                    <div key={f.id} className="sm:col-span-2">
                      <dt className="text-[10px] uppercase tracking-widest text-neutral-400 font-semibold">{f.label}</dt>
                      <dd>{v ? <a href={fileUrl(v)} target="_blank" rel="noreferrer"><img src={fileUrl(v)} alt="" className="max-h-56 rounded-xl border mt-1" /></a> : "—"}</dd>
                    </div>
                  );
                }
                return (
                  <div key={f.id}>
                    <dt className="text-[10px] uppercase tracking-widest text-neutral-400 font-semibold">{f.label}</dt>
                    <dd className="text-sm mt-0.5">{show(v) || "—"}</dd>
                  </div>
                );
              })}
              {extraKeys.map((k) => (
                <div key={k}><dt className="text-[10px] uppercase tracking-widest text-neutral-400 font-semibold">{k}</dt><dd className="text-sm">{show(qd.answers[k]) || "—"}</dd></div>
              ))}
            </dl>
          </Panel>
          <Panel title="Productos cotizados"><ItemsEditor items={qd.items || []} onChange={(v) => set("items", v)} products={products} /></Panel>
          <Panel title="Observaciones para el cliente"><Textarea rows={4} value={qd.admin_notes || ""} onChange={(e) => set("admin_notes", e.target.value)} placeholder="Condiciones, tiempos de elaboración, etc." data-testid="quote-admin-notes" /></Panel>
        </div>
        <div className="space-y-6">
          <Panel title="Estado">
            <select value={quoteStatus(qd.status).value} onChange={(e) => set("status", e.target.value)} className="w-full h-10 rounded-md border border-input px-3 text-sm bg-white" data-testid="quote-status-select">
              {QUOTE_STATUS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            {qd.order_id ? (
              <Link to={`/admin/pedidos/${qd.order_id}`} className="block mt-3 text-sm font-semibold text-[var(--brand-primary)]" data-testid="quote-order-link">Ver pedido {qd.order_code} →</Link>
            ) : (
              <Confirm onConfirm={convert} title="¿Convertir en pedido?" description="Se creará un pedido nuevo con los productos y totales de esta cotización, y se marcará como aceptada." confirmText="Convertir"
                trigger={<Button className="w-full mt-3 btn-accent" disabled={!(qd.items || []).length} data-testid="quote-convert"><ShoppingCart size={14} className="mr-1" /> Convertir en pedido</Button>} />
            )}
          </Panel>
          <Panel title="Cliente">
            <div className="space-y-3">
              <div><Label>Nombre</Label><Input value={qd.name || ""} onChange={(e) => set("name", e.target.value)} data-testid="quote-name-input" /></div>
              <div><Label>WhatsApp</Label><Input value={qd.whatsapp || ""} onChange={(e) => set("whatsapp", e.target.value)} data-testid="quote-whatsapp-input" /></div>
              <div><Label>Email</Label><Input value={qd.email || ""} onChange={(e) => set("email", e.target.value)} /></div>
              <div><Label>Tipo de cliente</Label><Input value={qd.customer_type || ""} onChange={(e) => set("customer_type", e.target.value)} /></div>
              <div><Label>Fecha del evento</Label><Input type="date" value={qd.event_date || ""} onChange={(e) => set("event_date", e.target.value)} /></div>
              <div><Label>Lugar de entrega</Label><Input value={qd.district || ""} onChange={(e) => set("district", e.target.value)} /></div>
            </div>
          </Panel>
          <Panel title="Totales">
            <TotalsBlock o={qd} set={set} />
            <div className="mt-3"><Label>Válida hasta</Label><Input type="date" value={qd.valid_until || ""} onChange={(e) => set("valid_until", e.target.value)} /></div>
          </Panel>
        </div>
      </div>
    </div>
  );
}
